/**
 * FLOW[27262]: MS229 - Enterprise-grade Message Service
 * Allows using antd message/notification/modal from non-React contexts
 * Must be initialized with App context at app startup
 */

import type { MessageInstance } from 'antd/es/message/interface';
import type { NotificationInstance } from 'antd/es/notification/interface';
import type { ModalStaticFunctions } from 'antd/es/modal/confirm';

// Singleton instances - will be set by AppProvider
let messageApi: MessageInstance | null = null;
let notificationApi: NotificationInstance | null = null;
let modalApi: Omit<ModalStaticFunctions, 'warn'> | null = null;

/**
 * Initialize the message service with App context APIs
 * Should be called once at app startup from within App component
 */
export function initMessageService(
  message: MessageInstance,
  notification: NotificationInstance,
  modal: Omit<ModalStaticFunctions, 'warn'>
) {
  messageApi = message;
  notificationApi = notification;
  modalApi = modal;
}

/**
 * Get the message API - falls back to console if not initialized
 */
export const messageService = {
  success: (content: string) => {
    if (messageApi) {
      messageApi.success(content);
    } else {
      console.log('[Message] Success:', content);
    }
  },
  error: (content: string) => {
    if (messageApi) {
      messageApi.error(content);
    } else {
      console.error('[Message] Error:', content);
    }
  },
  warning: (content: string) => {
    if (messageApi) {
      messageApi.warning(content);
    } else {
      console.warn('[Message] Warning:', content);
    }
  },
  info: (content: string) => {
    if (messageApi) {
      messageApi.info(content);
    } else {
      console.info('[Message] Info:', content);
    }
  },
  loading: (content: string) => {
    if (messageApi) {
      return messageApi.loading(content);
    } else {
      console.log('[Message] Loading:', content);
      return () => {};
    }
  },
};

/**
 * Get the notification API
 */
type NotificationArgs = {
  title: string;
  description?: string;
  duration?: number;
};

export const notificationService = {
  success: (args: NotificationArgs) => {
    if (notificationApi) {
      const { title, ...rest } = args;
      notificationApi.success({ ...rest, title });
    } else {
      console.log('[Notification] Success:', args.title, args.description);
    }
  },
  error: (args: NotificationArgs) => {
    if (notificationApi) {
      const { title, ...rest } = args;
      notificationApi.error({ ...rest, title });
    } else {
      console.error('[Notification] Error:', args.title, args.description);
    }
  },
  warning: (args: NotificationArgs) => {
    if (notificationApi) {
      const { title, ...rest } = args;
      notificationApi.warning({ ...rest, title });
    } else {
      console.warn('[Notification] Warning:', args.title, args.description);
    }
  },
  info: (args: NotificationArgs) => {
    if (notificationApi) {
      const { title, ...rest } = args;
      notificationApi.info({ ...rest, title });
    } else {
      console.info('[Notification] Info:', args.title, args.description);
    }
  },
};

/**
 * FLOW[27497]: MS232 - Get the modal API with dynamic access
 * Uses getter pattern to check modalApi at call time, not at module initialization
 */
export const modalService = {
  confirm: (config: Parameters<ModalStaticFunctions['confirm']>[0]) => {
    if (modalApi) {
      return modalApi.confirm(config);
    }
    console.log('[Modal] Confirm:', config.title, config.content);
    return { destroy: () => {}, update: () => {} };
  },
  info: (config: Parameters<ModalStaticFunctions['info']>[0]) => {
    if (modalApi) {
      return modalApi.info(config);
    }
    console.log('[Modal] Info:', config.title, config.content);
    return { destroy: () => {}, update: () => {} };
  },
  success: (config: Parameters<ModalStaticFunctions['success']>[0]) => {
    if (modalApi) {
      return modalApi.success(config);
    }
    console.log('[Modal] Success:', config.title, config.content);
    return { destroy: () => {}, update: () => {} };
  },
  error: (config: Parameters<ModalStaticFunctions['error']>[0]) => {
    if (modalApi) {
      return modalApi.error(config);
    }
    console.error('[Modal] Error:', config.title, config.content);
    return { destroy: () => {}, update: () => {} };
  },
  warning: (config: Parameters<ModalStaticFunctions['warning']>[0]) => {
    if (modalApi) {
      return modalApi.warning(config);
    }
    console.warn('[Modal] Warning:', config.title, config.content);
    return { destroy: () => {}, update: () => {} };
  },
};
